import React from 'react';

import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableBody,
  TableRow,
  TableCell,
  Text,
  Amount,
  Box,
  Card,
  CardBody,
} from '@razorpay/blade/components';
import { AssignedFlexibleComponentsTableDataType } from '../../types';
import { YearlyAmountColumn } from './YearlyAmountColumn';
import { isFBPUnitAmount } from 'components/PayDetails/utils';

export const FlexibleAssignmentsView = ({
  assignments,
  totalDeclared,
  totalAllocated,
  totalUnallocated,
}: {
  assignments?: AssignedFlexibleComponentsTableDataType[];
  totalDeclared: number;
  totalAllocated: number;
  totalUnallocated: number;
}) => {
  const showTierColumn = (assignments || []).some((row) => (row.tiers?.length ?? 0) > 0);

  return (
    <Box>
      <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap="spacing.4">
        <Card>
          <CardBody>
            <Text size="small" weight="regular" color="surface.text.gray.muted">
              Total Allocated amount
            </Text>
            <Amount testID="total-allocated" value={totalAllocated} />
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Text size="small" weight="regular" color="surface.text.gray.muted">
              FBP declaration
            </Text>
            <Amount testID="total-declared" value={totalDeclared} />
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Text size="small" weight="regular" color="surface.text.gray.muted">
              Unallocated amount
            </Text>
            <Amount testID="total-unallocated" value={totalUnallocated} />
          </CardBody>
        </Card>
      </Box>

      <Table
        data={{ nodes: assignments || [] }}
        display="grid"
        gridTemplateColumns={showTierColumn ? '4fr 2fr 2fr 2fr 2fr' : '4fr 2fr 2fr 2fr'}
        rowDensity="normal"
        marginTop={'spacing.7'}>
        {(tableData) => (
          <>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>
                  <Text weight="semibold">Component</Text>
                </TableHeaderCell>
                {showTierColumn && (
                  <TableHeaderCell>
                    <Text weight="semibold">Tier</Text>
                  </TableHeaderCell>
                )}
                <TableHeaderCell>
                  <Text weight="semibold">Max amount</Text>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Text weight="semibold">Monthly declaration</Text>
                </TableHeaderCell>
                <TableHeaderCell textAlign="right">
                  <Text weight="semibold">Yearly amount</Text>
                </TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>

            <TableBody>
              {tableData.map((rowData) => (
                <TableRow item={rowData} key={rowData.id}>
                  <TableCell>
                    <Text size="medium" weight="regular">
                      {rowData.display_name}
                    </Text>
                  </TableCell>
                  {showTierColumn && (
                    <TableCell>
                      <Text
                        size="medium"
                        weight="regular"
                        color={
                          rowData.selectedTierId
                            ? 'surface.text.gray.normal'
                            : 'surface.text.gray.muted'
                        }>
                        {/* The tier decides this employee's ceiling, so it
                            belongs beside the Max amount it explains.
                            "Not selected" and "—" are different states: the
                            first is a tiered component still waiting for
                            someone to choose a tier (its max reads ₹0 until
                            then), the second is a component with no tiers at
                            all. Showing a bare dash for both hid the fact that
                            a tier was still owed. */}
                        {(rowData.tiers?.length ?? 0) === 0
                          ? '—'
                          : (rowData.tiers?.find((tier) => tier.id === rowData.selectedTierId)
                              ?.label ?? 'Not selected')}
                      </Text>
                    </TableCell>
                  )}
                  <TableCell>
                    <Text>
                      {isFBPUnitAmount(rowData.unit) ? (
                        <Amount value={Number(rowData.maxValuePerMonth)} />
                      ) : (
                        `${rowData.maxValuePerMonth}${rowData.monthlyDeclaration.suffix}`
                      )}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text>
                      {isFBPUnitAmount(rowData.unit) ? (
                        <Amount value={Number(rowData.monthlyDeclaration.value)} />
                      ) : (
                        `${rowData.monthlyDeclaration.value}${rowData.monthlyDeclaration.suffix}`
                      )}
                    </Text>
                  </TableCell>
                  <TableCell textAlign="right">
                    <YearlyAmountColumn
                      rowData={rowData}
                      monthlyDeclarationValue={rowData.monthlyDeclaration.value}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </>
        )}
      </Table>
    </Box>
  );
};
